import { type ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react';
import { Menu, X } from 'lucide-react';
import { Button } from '../../atoms/Button/Button';
import { useTranslation } from '../../../i18n/useTranslation';
import { LicenseRequiredNotice } from '../../layout/LicenseRequiredNotice/LicenseRequiredNotice';
import { TermsReadOnlyNotice } from '../../layout/TermsReadOnlyNotice/TermsReadOnlyNotice';
import { PageErrorBoundary } from '../../errors/ErrorBoundary';
import { OnboardingAutoStart } from '../../onboarding/OnboardingAutoStart';
import { FeedbackAutoStart } from '../../appFeedback/FeedbackContext';
import { OptInPromptAutoStart } from '../../optInPrompts/OptInPromptContext';
import { SectionTabs } from '../../layout/SectionTabs/SectionTabs';
import { MobileTabBar } from '../../layout/MobileTabBar/MobileTabBar';
import { CaptureDock } from '../../capture/CaptureDock';
import './AppLayout.css';

export interface AppLayoutProps {
  header?: ReactNode;
  sidebar?: ReactNode;
  main: ReactNode;
  footer?: ReactNode;
}

/** Id of the `<main>` landmark — target of the skip link. */
export const MAIN_CONTENT_ID = 'main-content';

/**
 * Application shell. Owns the page landmarks (banner, navigation region,
 * main, contentinfo), the "skip to main content" link (WCAG 2.4.1) and, on
 * narrow screens, the disclosure that shows the sidebar navigation as a
 * drawer so no functionality is lost at 320 px (WCAG 1.4.10).
 */
export function AppLayout({ header, sidebar, main, footer }: AppLayoutProps) {
  const { t } = useTranslation();
  const [navOpen, setNavOpen] = useState(false);
  const sidebarId = useId();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);

  const closeNav = useCallback((restoreFocus: boolean) => {
    setNavOpen(false);
    if (restoreFocus) toggleRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!navOpen) return undefined;
    const aside = sidebarRef.current;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') closeNav(true);
    };
    // Following a navigation link closes the drawer (click also fires for Enter).
    const onClick = (e: MouseEvent): void => {
      if ((e.target as Element | null)?.closest('a[href]')) closeNav(false);
    };
    document.addEventListener('keydown', onKey);
    aside?.addEventListener('click', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      aside?.removeEventListener('click', onClick);
    };
  }, [navOpen, closeNav]);

  const hasSidebar = sidebar !== undefined;

  return (
    <div className="rg-app-layout" data-testid="app-layout">
      <OnboardingAutoStart />
      <OptInPromptAutoStart />
      <FeedbackAutoStart />
      <a className="rg-app-layout__skip-link" href={`#${MAIN_CONTENT_ID}`}>
        {t('nav.skipToContent')}
      </a>
      {hasSidebar && (
        <aside
          ref={sidebarRef}
          id={sidebarId}
          className={`rg-app-layout__sidebar${navOpen ? ' rg-app-layout__sidebar--open' : ''}`}
          data-testid="app-layout-sidebar"
        >
          {sidebar}
        </aside>
      )}
      <div className="rg-app-layout__body" data-testid="app-layout-body">
        {(header !== undefined || hasSidebar) && (
          <header className="rg-app-layout__header" data-testid="app-layout-header">
            {hasSidebar && (
              <Button
                ref={toggleRef}
                variant="ghost"
                className="rg-app-layout__nav-toggle"
                aria-expanded={navOpen}
                aria-controls={sidebarId}
                aria-label={navOpen ? t('nav.closeMenu') : t('nav.openMenu')}
                onClick={() => (navOpen ? closeNav(false) : setNavOpen(true))}
              >
                {navOpen ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
              </Button>
            )}
            {header !== undefined && <div className="rg-app-layout__header-content">{header}</div>}
          </header>
        )}
        <main id={MAIN_CONTENT_ID} tabIndex={-1} className="rg-app-layout__main" data-testid="app-layout-main">
          <LicenseRequiredNotice />
          <TermsReadOnlyNotice />
          {/* The current area's pages as tabs (usability audit §4.1). */}
          {hasSidebar && <SectionTabs />}
          {/* A crashing page shows an error card here; header and sidebar stay. */}
          <PageErrorBoundary>{main}</PageErrorBoundary>
        </main>
        {footer != null && (
          <footer className="rg-app-layout__footer" data-testid="app-layout-footer">
            {footer}
          </footer>
        )}
      </div>
      {/* Voice notes: recorder bar, "Note ready" banner and notices (pinned above the tab bar). */}
      <CaptureDock />
      {/* Phones: Heute · Wasser · Erfassen · Tiere · Mehr (opens the drawer). */}
      {hasSidebar && (
        <MobileTabBar drawerId={sidebarId} drawerOpen={navOpen} onMore={() => (navOpen ? closeNav(false) : setNavOpen(true))} />
      )}
    </div>
  );
}
